/**
 * The example a mapping editor shows beside its rows: the dataset's first item,
 * or — for a live dataset — the newest trace its source matches (the first
 * case "Test on one case" offers). Loaded only when an editor is open.
 */

import { useDatasetItems, useDatasetSource } from "@/features/datasets/hooks";
import { useEvaluatorSample, type SampleRef } from "@/features/evaluators/hooks";
import { mappingView } from "@/features/evaluators/sample-paths";
import { useRecentTraces } from "@/features/traces/hooks";
import { fromFilters } from "@/features/traces/trace-query";

export interface ExampleScope {
  datasetId: string | null;
  version: number | null;
  live: boolean;
}

export function useExampleCase({ datasetId, version, live }: ExampleScope) {
  const id = datasetId ?? "";
  const item = useDatasetItems(id, datasetId && !live ? version : null, 1).data?.items[0];
  const source = useDatasetSource(id, !!datasetId && live).data;
  const trace = useRecentTraces(source?.connection_id ?? "", fromFilters(source?.filters)).data?.items[0];
  const ref: SampleRef =
    live && source && trace
      ? {
          connection_id: source.connection_id,
          trace_id: trace.id,
          ...(trace.observation_id && { observation_id: trace.observation_id }),
        }
      : !live && item
        ? { dataset_item_id: item.id }
        : {};
  const raw = useEvaluatorSample(ref).data;
  return {
    /** As shown: an observation raw, as Langfuse returns it, or the item's document. */
    raw,
    /** What mapping paths read (`valueAt(view, "meta.agent")`). */
    view: raw && live ? mappingView(raw) : raw,
    live,
  };
}
